/**
 * FieldList Component
 *
 * Smart component for managing a list of fields with survey-type-specific behavior.
 * Handles field operations and provides integration points for custom logic (like SatisfactionSurvey metadata).
 */

import { FormFieldType, IFormField } from '@ViewModels';
import * as React from 'react';
import { questionTypeOptionsFromFieldTypes } from '../../FormFieldWithOptions/questionTypes';
import {
	ConditionalQuestionControlsProps,
	ConditionalVisibilityProps,
	CustomFieldManagerProps,
	FieldListProps,
	TagManagerProps,
} from '../types';
import { QuestionField } from './QuestionField';

/**
 * FieldList component for managing a collection of form fields
 */
export const FieldList: React.FC<FieldListProps> = React.memo(
	({
		fields,
		isEdit,
		isClone = false,
		config,
		formState,
		tagGenerationSettings,
		customFieldMap,
		availableCustomFields = [],
		onFieldUpdate,
		onFieldRemove,
		onLabelChange,
		onToggleOptional,
		onFieldTypeChange,
		onTagPrefixChange,
		onTagPrefixDelete,
		onCustomFieldChange,
		onCustomFieldDelete,
		onConditionalToggle,
		onRatingThresholdChange,
		getFieldMetadata,
		isFieldDirty,
		onValidationTrigger,
		templateMode,
		conditionalVisibilityMap,
		onConditionalVisibilityToggle,
		onConditionalVisibilityQuestionSelect,
		onConditionalVisibilityAnswerSelect,
		onConditionalVisibilityAnswerAdd,
		onConditionalVisibilityAnswerRemove,
	}) => {
		// Memoize form state errors to avoid recalculating on every render
		const formErrors = React.useMemo(() => {
			const customFormErrors = formState.errors?.customForm;
			// Type guard to check if customFormErrors has fields property
			if (!customFormErrors || typeof customFormErrors !== 'object' || !('fields' in customFormErrors)) {
				return [];
			}
			const fieldsArray = (customFormErrors as { fields?: unknown }).fields;
			return Array.isArray(fieldsArray) ? fieldsArray : [];
		}, [formState.errors?.customForm]);

		// Memoize tag generation settings to avoid recreating objects
		const memoizedTagGenerationSettings = React.useMemo(() => tagGenerationSettings, [tagGenerationSettings]);

		// Memoize conditional questions config
		const conditionalConfig = React.useMemo(() => config.conditionalQuestions, [config.conditionalQuestions]);

		// Memoize conditional visibility config ("Only show when")
		const conditionalVisibilityConfig = React.useMemo(
			() => config.conditionalVisibility,
			[config.conditionalVisibility]
		);

		// Memoize tag generation config to avoid object recreation
		const tagGenerationConfig = React.useMemo(
			() => ({
				enabled: config.tagGeneration.enabled,
				supportsTagPrefixes: config.tagGeneration.supportsTagPrefixes,
			}),
			[config.tagGeneration.enabled, config.tagGeneration.supportsTagPrefixes]
		);

		// Memoize custom field management config
		const customFieldConfig = React.useMemo(
			() => ({
				enabled: config.customFieldManagement.enabled,
				supportedFieldTypes: config.customFieldManagement.supportedFieldTypes,
			}),
			[config.customFieldManagement.enabled, config.customFieldManagement.supportedFieldTypes]
		);

		// Memoize custom field settings to avoid recreating objects
		const memoizedCustomFieldMap = React.useMemo(() => customFieldMap, [customFieldMap]);

		// Calculate used custom field IDs for disabled state
		const usedCustomFieldIds = React.useMemo(() => {
			if (!memoizedCustomFieldMap) return [];
			return memoizedCustomFieldMap.map((mapping: { contactFieldId: string }) => mapping.contactFieldId);
		}, [memoizedCustomFieldMap]);

		// Render individual field with proper configuration - optimized with minimal dependencies
		const renderField = React.useCallback(
			(field: IFormField<string>, index: number) => {
				// Calculate field state - simplified and memoized
				const fieldErrors = formErrors[index] || {};
				const hasError = fieldErrors.label?.message || fieldErrors.options?.message;
				const hasBeenSaved = !!field._type && !isClone;
				const isDirty = isFieldDirty(field.id, index);

				// Create TagManager props if tag generation is enabled AND this is an option field
				let tagManagerProps: TagManagerProps | undefined;
				const isOptionField =
					field.fieldType === FormFieldType.MultipleOptions || field.fieldType === FormFieldType.Option;

				if (
					tagGenerationConfig.enabled &&
					tagGenerationConfig.supportsTagPrefixes &&
					isOptionField &&
					onTagPrefixChange &&
					onTagPrefixDelete
				) {
					tagManagerProps = {
						fieldId: field.id,
						fieldLabel: field.label,
						isOptionField,
						questionIndex: index,
						tagGenerationSettings: memoizedTagGenerationSettings,
						onTagPrefixChange,
						onTagPrefixDelete,
						disabled: false, // Allow tag creation for new surveys, existing surveys, and clones
						onValidationTrigger,
					};
				}

				// Create CustomFieldManager props if custom field management is enabled AND field type is supported
				let customFieldManagerProps: CustomFieldManagerProps | undefined;
				const isSupportedFieldType = customFieldConfig.supportedFieldTypes.includes(field.fieldType);

				if (
					customFieldConfig.enabled &&
					isSupportedFieldType &&
					onCustomFieldChange &&
					onCustomFieldDelete
					// Temporarily removed field.label?.trim() requirement for debugging
				) {
					// Filter out the current field's mapping from used IDs to allow editing existing mappings
					const currentFieldMapping = memoizedCustomFieldMap?.find(mapping => mapping.fieldId === field.id);
					const filteredUsedCustomFieldIds = usedCustomFieldIds.filter(
						usedId => usedId !== currentFieldMapping?.contactFieldId
					);

					customFieldManagerProps = {
						fieldId: field.id,
						fieldLabel: field.label,
						isOptionField,
						customFieldMap: memoizedCustomFieldMap,
						onCustomFieldChange,
						onCustomFieldDelete,
						disabled: false, // Allow custom field creation for new surveys, existing surveys, and clones
						availableCustomFields, // Pass available custom fields from API
						usedCustomFieldIds: filteredUsedCustomFieldIds, // Pass filtered used field IDs for disabled state
						onValidationTrigger,
						questionIndex: index,
					};
				}

				// Create ConditionalQuestionControls props if conditional questions are enabled
				let conditionalQuestionProps: ConditionalQuestionControlsProps | undefined;
				if (conditionalConfig?.enabled && onConditionalToggle && onRatingThresholdChange && getFieldMetadata) {
					const metadata = getFieldMetadata(field.id);
					conditionalQuestionProps = {
						field,
						fieldIndex: index,
						config: conditionalConfig,
						metadata,
						onConditionalToggle,
						onRatingThresholdChange,
					};
				}

				// Create ConditionalVisibility props for surveys that support it
				let conditionalVisibilityProps: ConditionalVisibilityProps | undefined;
				if (
					conditionalVisibilityConfig?.enabled &&
					onConditionalVisibilityToggle &&
					onConditionalVisibilityQuestionSelect &&
					onConditionalVisibilityAnswerSelect &&
					onConditionalVisibilityAnswerAdd &&
					onConditionalVisibilityAnswerRemove
				) {
					conditionalVisibilityProps = {
						field,
						fieldIndex: index,
						allFields: fields,
						state: conditionalVisibilityMap?.get(field.id),
						onToggle: onConditionalVisibilityToggle,
						onQuestionSelect: onConditionalVisibilityQuestionSelect,
						onAnswerSelect: onConditionalVisibilityAnswerSelect,
						onAnswerAdd: onConditionalVisibilityAnswerAdd,
						onAnswerRemove: onConditionalVisibilityAnswerRemove,
					};
				}

				return (
					<QuestionField
						key={`${field.id}-question-${index}`}
						field={field}
						questionIndex={index}
						isEdit={isEdit}
						isDirty={isDirty}
						hasError={!!hasError}
						hasBeenSaved={hasBeenSaved}
						tagManagerProps={tagManagerProps}
						customFieldManagerProps={customFieldManagerProps}
						conditionalQuestionProps={conditionalQuestionProps}
						conditionalVisibilityProps={conditionalVisibilityProps}
						onFieldUpdate={onFieldUpdate}
						onFieldRemove={onFieldRemove}
						onLabelChange={onLabelChange}
						onToggleOptional={onToggleOptional}
						onFieldTypeChange={onFieldTypeChange}
						onValidationTrigger={onValidationTrigger}
						templateMode={templateMode}
						// Restricts the question-type dropdown to the survey's supported types,
						// mapped from the legacy `supportedFieldTypes` to their QuestionTypes (1:1).
						// Checked independently of `enabled` (which gates add/remove/reorder).
						formFieldOptions={
							customFieldConfig?.supportedFieldTypes?.length
								? questionTypeOptionsFromFieldTypes(customFieldConfig.supportedFieldTypes)
								: undefined
						}
					/>
				);
			},
			[
				formErrors,
				isClone,
				isFieldDirty,
				tagGenerationConfig,
				memoizedTagGenerationSettings,
				customFieldConfig,
				memoizedCustomFieldMap,
				availableCustomFields,
				usedCustomFieldIds,
				conditionalConfig,
				conditionalVisibilityConfig,
				onTagPrefixChange,
				onTagPrefixDelete,
				onCustomFieldChange,
				onCustomFieldDelete,
				onConditionalToggle,
				onRatingThresholdChange,
				getFieldMetadata,
				isEdit,
				onFieldUpdate,
				onFieldRemove,
				onLabelChange,
				onToggleOptional,
				onFieldTypeChange,
				onValidationTrigger,
				templateMode,
				conditionalVisibilityMap,
				onConditionalVisibilityToggle,
				onConditionalVisibilityQuestionSelect,
				onConditionalVisibilityAnswerSelect,
				onConditionalVisibilityAnswerAdd,
				onConditionalVisibilityAnswerRemove,
				fields,
			]
		);

		return (
			<>
				{/* Render all fields */}
				{fields.map(renderField)}
				{/*
				 * "+ Add Question" used to render here. It has been lifted into
				 * `AdditionalQuestions` so it can sit side-by-side with
				 * `SectionsActionBar`'s "+ Add Section" at the bottom of V1.
				 */}
			</>
		);
	}
);

FieldList.displayName = 'FieldList';
